import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// ✅ Helpers
export const statusNumberToString = (
  value: string | number
): "active" | "resolved" | "chronic" => {
  switch (value) {
    case 1:
    case "chronic":
      return "chronic";
    case 2:
    case "active":
      return "active";
    case 3:
    case "resolved":
      return "resolved";
    default:
      return "chronic";
  }
};

export const statusStringToNumber = (value: string | number): number => {
  switch (value) {
    case "chronic":
    case 1:
      return 1;
    case "active":
    case 2:
      return 2;
    case "resolved":
    case 3:
      return 3;
    default:
      return 1;
  }
};

// ✅ Zod Schema
// Lengths match the API's RelatedCondition model (add-patient-condition-related).
export const CONDITION_NAME_MAX_LENGTH = 500;
export const CONDITION_NOTES_MAX_LENGTH = 5000;

export const conditionSchema = z.object({
  conditionName: z
    .string()
    .min(1, "Condition name is required")
    .max(CONDITION_NAME_MAX_LENGTH, `Condition name must be ${CONDITION_NAME_MAX_LENGTH} characters or fewer`),
  status: z.enum(["chronic", "active", "resolved"]),
  diagnosedDate: z.string().optional(),
  notes: z
    .string()
    .max(CONDITION_NOTES_MAX_LENGTH, `Additional notes must be ${CONDITION_NOTES_MAX_LENGTH} characters or fewer`)
    .optional(),
});

export type ConditionForm = z.infer<typeof conditionSchema>;

export interface ConditionEditMode {
  id: string;
  conditionName: string;
  status: "chronic" | "active" | "resolved";
  diagnosedDate?: string;
  notes?: string;
}

export const statusOptions = [
  { value: "chronic", label: "Chronic" },
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
];

// ✅ Helper for displaying color in UI
export const getStatusColorClass = (status: string) => {
  switch (status.toLowerCase()) {
    case "chronic":
      return "bg-orange-100 text-orange-700";
    case "active":
      return "bg-green-100 text-green-700";
    case "resolved":
      return "bg-gray-100 text-gray-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const normalizeDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const useConditionFormState = (editMode?: ConditionEditMode | any) => {
  const [open, setOpen] = useState(false);

  const formMethods = useForm<ConditionForm>({
    resolver: zodResolver(conditionSchema),
    defaultValues: {
      status: "active",
    },
  });

  const { reset } = formMethods;

  useEffect(() => {
    if (editMode) {
      reset({
        conditionName: editMode.conditionName,
        status: statusNumberToString(editMode.status),
        diagnosedDate: normalizeDate(editMode.diagnosedDate),
        notes: editMode.notes || "",
      });
    } else {
      reset({
        conditionName: "",
        status: "active",
        diagnosedDate: "",
        notes: "",
      });
    }
  }, [editMode, reset]);

  return {
    open,
    setOpen,
    ...formMethods,
  };
};

export const transformConditionForBackend = (condition: ConditionForm) => {
  return {
    ...condition,
    status: statusStringToNumber(condition.status),
  };
};

export const handleSubmit = (
  data: ConditionForm,
  onSave: (condition: ConditionForm) => void,
  setOpen: (open: boolean) => void,
  reset: () => void
) => {
  onSave(data);
  setOpen(false);
  reset();
};
